// CTW-DURMAH-STUDENT-RESOURCES-INTERNSHIP-READINESS-AND-YEAR-AWARE-NUDGES-01:
// Shared presentational primitives for the public Student Resources pages
// (/resources/*). Kept deliberately small: hero, breadcrumb row, sources
// block, cross-link cards, disclaimer and a soft CTA. Content comes from
// the data modules in src/content/resources.

import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { ArrowRight, BookOpen, ExternalLink } from "lucide-react";
import type { ResourceSourceGroup, ResourceTipLink } from "@/content/resources/types";
import {
  campaignParamsFromQuery,
  withCampaignParams,
} from "@/lib/marketing/campaignAttribution";
import { trackMarketingEngagement } from "@/lib/analytics/marketingEngagement";

// Inline "Go to the source" row rendered at the point of mention inside a
// section or tip, so students can click straight through to the primary /
// official material. Outbound links only, new tab, copyright-safe.
export function SectionSourceLinks({ links }: { links?: ResourceTipLink[] }) {
  if (!links || links.length === 0) return null;
  return (
    <div className="mt-5 rounded-xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/60 dark:bg-indigo-500/5 p-4">
      <p className="mb-2 text-xs font-black uppercase tracking-widest text-indigo-700 dark:text-indigo-400">
        Go to the source
      </p>
      <ul className="flex flex-col gap-1.5">
        {links.map((l) => (
          <li key={l.href}>
            <a
              href={l.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-start gap-1.5 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:underline"
            >
              <ExternalLink aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {l.label}
                <span className="sr-only"> (opens in a new tab)</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ResourceBreadcrumbs({ current }: { current: string }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-6 text-sm text-gray-500 dark:text-slate-400">
      <ol className="flex flex-wrap items-center gap-2">
        <li>
          <Link href="/resources" prefetch={false} className="hover:text-indigo-600 transition-colors font-medium">
            Student Resources
          </Link>
        </li>
        <li aria-hidden className="text-gray-300">/</li>
        <li aria-current="page" className="font-semibold text-gray-700 dark:text-slate-200">
          {current}
        </li>
      </ol>
    </nav>
  );
}

export function ResourceHero({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: React.ReactNode;
  subtitle: string;
}) {
  return (
    <header className="mb-12">
      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 text-[10px] font-black uppercase tracking-[0.2em] mb-4 border border-indigo-100 dark:border-indigo-500/20">
        {eyebrow}
      </span>
      <h1 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white mb-5 tracking-tight leading-tight">
        {title}
      </h1>
      <p className="text-lg md:text-xl text-gray-600 dark:text-slate-400 leading-relaxed font-medium max-w-3xl">
        {subtitle}
      </p>
    </header>
  );
}

export function SourcesSection({
  groups,
  intro,
}: {
  groups: ResourceSourceGroup[];
  intro?: string;
}) {
  return (
    <section
      id="sources"
      className="mt-16 rounded-3xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-8 md:p-10"
    >
      <div className="flex items-center gap-3 mb-3">
        <BookOpen className="w-5 h-5 text-indigo-600" />
        <h2 className="text-xl font-black text-gray-900 dark:text-white m-0">
          Sources and further reading
        </h2>
      </div>
      <p className="text-sm text-gray-600 dark:text-slate-400 mb-6 max-w-3xl">
        {intro ||
          "Use these sources for further reading and check their date and scope. Your module handbook and assessment brief set your course's requirements; official court, legislative and professional guidance should be checked for the jurisdiction and date relevant to your work."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {groups.map((group) => (
          <div key={group.topic}>
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500 mb-2">
              {group.topic}
            </h3>
            <ul className="space-y-1.5">
              {group.sources.map((s) => (
                <li key={s.href}>
                  <a
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{s.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

export function ResourceDisclaimer() {
  return (
    <p className="mt-8 text-xs text-gray-500 dark:text-slate-500 leading-relaxed max-w-3xl">
      This guide is for general educational purposes only. It is not legal
      advice, and it does not guarantee any academic, admissions or career
      outcome. Expectations vary between universities, firms, chambers and
      years, so always check your own institution's guidance, each employer's
      published information, and your university careers service. Durmah is
      independent and is not affiliated with or endorsed by any university,
      regulator or employer.
    </p>
  );
}

export interface CrossLinkItem {
  title: string;
  description: string;
  href: string;
}

export function ResourceCrossLinks({
  heading = "Keep going",
  items,
}: {
  heading?: string;
  items: CrossLinkItem[];
}) {
  return (
    <section className="mt-12">
      <h2 className="text-xl font-black text-gray-900 dark:text-white mb-6">{heading}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            prefetch={false}
            className="group flex flex-col rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 p-6 shadow-sm hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-500/40 transition-all"
          >
            <h3 className="font-bold text-gray-900 dark:text-white mb-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {item.title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-400 flex-1 leading-relaxed">
              {item.description}
            </p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-black text-indigo-600 dark:text-indigo-400 group-hover:gap-2 transition-all">
              Read this <ArrowRight className="w-4 h-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function ResourceSoftCta({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  const router = useRouter();
  const trialHref = withCampaignParams(
    "/login?intent=signup",
    campaignParamsFromQuery(router.query),
  );

  return (
    <section className="mt-14 rounded-3xl bg-gray-950 p-8 md:p-12 text-white relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-transparent" />
      <div className="relative z-10 max-w-2xl">
        <h2 className="text-2xl md:text-3xl font-black mb-3 tracking-tight">{title}</h2>
        <p className="text-indigo-100/70 font-medium mb-7 leading-relaxed">{body}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            href={trialHref}
            prefetch={false}
            onClick={() =>
              trackMarketingEngagement("trial_start", {
                source: "resource_guide",
              })
            }
            className="inline-flex items-center justify-center rounded-xl bg-indigo-600 hover:bg-indigo-700 px-7 py-3.5 font-bold transition-colors"
          >
            Start 14-day free trial
          </Link>
          <Link
            href="/why-durmah"
            prefetch={false}
            className="inline-flex items-center justify-center rounded-xl border border-white/20 hover:bg-white/10 px-7 py-3.5 font-bold transition-colors"
          >
            Why Durmah exists
          </Link>
        </div>
      </div>
    </section>
  );
}
